import {ParameterList, ParameterValues} from '@/model/api/parameters';
import {isEqual} from 'lodash-es';
import {FoundDifferences} from '@/components/estimates/common/segments/ParameterFoundValues';
import {
    buildFoundParts,
    BuildingPartNote,
    composeFloorArea,
    defaultPartChoice,
    FoundPart,
    isPartList,
    PartChoice,
} from './floorAreaParts';

export interface FoundValueOrigin {
    sourceId: string;

    /** Words of the source the value follows from, null for a source processed before the quotes were kept */
    quote: string | null;

    page: number | null;

    /** Of a floor area: what the drawing tells of its parts, in their order */
    buildingPartNotes?: BuildingPartNote[];
}

export interface FoundValue {
    /** Raw value as the form holds it */
    value: any;

    /** False when the form would not accept the value */
    valid: boolean;

    /** Some of the sources of the value was not seen by the user yet */
    new: boolean;

    origins: FoundValueOrigin[];
}

export interface FoundParameter {
    name: string;

    /** The different values the sources give */
    values: FoundValue[];
}

export interface FoundOption {
    value: any;
    valid: boolean;
    isNew: boolean;
    origins: FoundValueOrigin[];
    /** The value is in the form already */
    equalsCurrent: boolean;
}

export interface FoundItem {
    /** An item of a multiple choice the form does not have yet */
    item: any;
    isNew: boolean;
    origins: FoundValueOrigin[];
}

export interface FoundRow {
    name: string;
    type: string;
    /** The value in the form, also when not saved yet */
    current: any;
    /** A floor area of parts of the building is composed of the parts of the sources */
    kind: 'single' | 'multi' | 'parts';
    /** Single value: the values that differ from the form, with the one the form has when the sources disagree */
    options: FoundOption[];
    /** The sources give different values, the user must pick one */
    conflict: boolean;
    /** Multiple choice: the items the sources add */
    items: FoundItem[];
    /** Floor area: the parts of the building the sources found */
    parts: FoundPart[];
    isNew: boolean;
}

export interface SameRow {
    name: string;
    current: any;
    origins: FoundValueOrigin[];
}

export interface FoundRows {
    /** Values that differ from the form, to decide on */
    rows: FoundRow[];
    /** Values the form already has */
    same: SameRow[];
}

/** What the user picked in a row: the value of a single value, the items of a multiple choice, the parts of a floor area */
export type RowSelection = {kind: 'single', value: any | undefined}
    | {kind: 'multi', items: any[]}
    | {kind: 'parts', parts: Record<string, PartChoice>};

function comparable<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
}

export function valuesEqual(a: any, b: any): boolean {
    if (Array.isArray(a) && Array.isArray(b) && a.every(item => typeof item !== 'object')
        && b.every(item => typeof item !== 'object')) {
        return isEqual([...a].sort(), [...b].sort());
    }

    return isEqual(comparable(a), comparable(b));
}

function isEmpty(value: any): boolean {
    return value === null || value === undefined || (Array.isArray(value) && value.length === 0);
}

/**
 * Compares the values found in the sources with the form
 *
 * @param sourceId shows only the values of this source when given
 */
export function buildFoundRows(
    parameters: FoundParameter[],
    definitions: ParameterList,
    current: ParameterValues,
    sourceId: string | null,
): FoundRows {
    const rows: FoundRow[] = [];
    const same: SameRow[] = [];

    for (const parameter of parameters) {
        const definition = definitions[parameter.name];

        if (definition === undefined) {
            continue;
        }

        const values = parameter.values
            .map(value => sourceId === null
                ? value
                : {...value, origins: value.origins.filter(origin => origin.sourceId === sourceId)})
            .filter(value => value.origins.length > 0);

        if (values.length === 0) {
            continue;
        }

        const currentValue = current[parameter.name] ?? null;
        const sameOrigins = values
            .filter(value => !isEmpty(currentValue) && valuesEqual(value.value, currentValue))
            .flatMap(value => value.origins);

        let row: FoundRow | null;

        if (LIST_TYPES.includes(definition.type)) {
            row = buildMultiRow(parameter.name, definition.type, currentValue, values);
        } else if (definition.type === 'floor-area' && values.some(value => value.valid && isPartList(value.value))) {
            row = buildPartsRow(parameter.name, definition.type, currentValue, values);
        } else {
            row = buildSingleRow(parameter.name, definition.type, currentValue, values);
        }

        if (row !== null) {
            rows.push(row);
        } else {
            same.push({name: parameter.name, current: currentValue, origins: sameOrigins});
        }
    }

    return {rows, same};
}

function buildSingleRow(name: string, type: string, current: any, values: FoundValue[]): FoundRow | null {
    const options = values.map(value => ({
        value: value.value,
        valid: value.valid,
        isNew: value.new,
        origins: value.origins,
        equalsCurrent: !isEmpty(current) && valuesEqual(value.value, current),
    }));

    const differing = options.filter(option => !option.equalsCurrent);

    if (differing.length === 0) {
        return null;
    }

    const conflict = options.filter(option => option.valid).length > 1;

    return {
        name,
        type,
        current,
        kind: 'single',
        options: conflict ? options : differing,
        conflict,
        items: [],
        parts: [],
        isNew: differing.some(option => option.isNew),
    };
}

/** Parameters of a list, each source adds its items */
const LIST_TYPES = ['multi-enum', 'multi-text', 'reconstruction-list', 'access-land-list', 'parcel-numbers'];

/**
 * An item of a list is known by what identifies it, e.g. a parcel by its number, not by its note
 */
function sameItem(type: string, a: any, b: any): boolean {
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
        return valuesEqual(a, b);
    }

    switch (type) {
        case 'parcel-numbers':
            return String(a.number).replace(/\s+/g, '') === String(b.number).replace(/\s+/g, '');
        case 'reconstruction-list':
            return a.type === b.type && a.year === b.year;
        case 'access-land-list':
            return a.prefixNumber === b.prefixNumber && (a.suffixNumber ?? null) === (b.suffixNumber ?? null);
        default:
            return valuesEqual(a, b);
    }
}

function buildMultiRow(name: string, type: string, current: any, values: FoundValue[]): FoundRow | null {
    const currentItems: any[] = Array.isArray(current) ? current : [];
    const items: FoundItem[] = [];

    for (const value of values.filter(found => found.valid && Array.isArray(found.value))) {
        for (const item of value.value as any[]) {
            if (currentItems.some(currentItem => sameItem(type, currentItem, item))) {
                continue;
            }

            const known = items.find(found => sameItem(type, found.item, item));

            if (known === undefined) {
                items.push({item, isNew: value.new, origins: [...value.origins]});
            } else {
                known.isNew = known.isNew || value.new;
                known.origins.push(...value.origins);
            }
        }
    }

    if (items.length === 0) {
        return null;
    }

    return {
        name,
        type,
        current,
        kind: 'multi',
        options: [],
        conflict: false,
        items,
        parts: [],
        isNew: items.some(item => item.isNew),
    };
}

function buildPartsRow(name: string, type: string, current: any, values: FoundValue[]): FoundRow | null {
    const parts = buildFoundParts(values, current);

    if (parts.every(part => part.inForm)) {
        return null;
    }

    return {
        name,
        type,
        current,
        kind: 'parts',
        options: [],
        conflict: false,
        items: [],
        parts,
        isNew: parts.some(part => part.isNew && !part.inForm),
    };
}

/**
 * Picks what is safe to take over: a new value of an empty field the sources agree on, the new items of a choice;
 * never a value that overwrites the form, nor one of disagreeing sources
 */
export function defaultSelection(row: FoundRow): RowSelection {
    if (row.kind === 'multi') {
        return {kind: 'multi', items: row.items.filter(item => item.isNew).map(item => item.item)};
    }

    if (row.kind === 'parts') {
        return {
            kind: 'parts',
            parts: Object.fromEntries(row.parts.map(part => [part.key, defaultPartChoice(part, row.current)])),
        };
    }

    const only = row.options.length === 1 ? row.options[0] : null;

    return {
        kind: 'single',
        value: only !== null && !row.conflict && only.valid && only.isNew && isEmpty(row.current) ? only.value : undefined,
    };
}

/**
 * The values to save: the picked value, or the items of the form with the picked items
 */
export function selectedValues(rows: FoundRow[], selections: Record<string, RowSelection>): ParameterValues {
    const values: ParameterValues = {};

    for (const row of rows) {
        const selection = selections[row.name];

        if (selection === undefined) {
            continue;
        }

        if (selection.kind === 'single' && selection.value !== undefined) {
            values[row.name] = selection.value;
        }

        if (selection.kind === 'multi' && selection.items.length > 0) {
            values[row.name] = [...(Array.isArray(row.current) ? row.current : []), ...selection.items];
        }

        if (selection.kind === 'parts') {
            const composed = composeFloorArea(row.current, row.parts, selection.parts);

            if (composed !== null && !valuesEqual(composed, row.current)) {
                values[row.name] = composed;
            }
        }
    }

    return values;
}

/**
 * What the sources offer for a field beside what it has: the values that can be taken over,
 * the items a multiple choice does not have, the parts of the building a floor area does not have
 */
export function describeDifferences(parameter: FoundParameter, definitions: ParameterList, current: any): FoundDifferences {
    const row = buildFoundRows([parameter], definitions, {[parameter.name]: current}, null).rows[0];

    if (row === undefined) {
        return {count: 0, kind: 'values'};
    }

    if (row.kind === 'parts') {
        return {count: row.parts.filter(part => !part.inForm).length, kind: 'parts'};
    }

    return row.kind === 'multi'
        ? {count: row.items.length, kind: 'items'}
        : {count: row.options.filter(option => option.valid && !option.equalsCurrent).length, kind: 'values'};
}
